import type { DmScanInstruction, DmScanSummary } from './types';

/** What the UI shows for a scan: DM's decision, or a neutral series start. */
export type ScanDecision = DmScanInstruction | 'start';

export function isComfortScan(scan: Pick<DmScanSummary, 'type'>): boolean {
    return scan.type === 'comfort';
}

export function scanDecision(
    scan: Pick<DmScanSummary, 'type' | 'instruction' | 'seriesStart'>
): ScanDecision | null {
    if (isComfortScan(scan)) return null;
    return scan.seriesStart ? 'start' : scan.instruction;
}

/** Header and marker wording; `sequence` numbers the scans of a case with no aligners. */
export function scanTitle(
    scan: Pick<DmScanSummary, 'type' | 'alignerIndex'>,
    tracksAligners: boolean,
    sequence: number | null = null
): string {
    if (isComfortScan(scan)) return 'Comfort scan';
    if (tracksAligners) return `Aligner ${scan.alignerIndex ?? '?'}`;
    return sequence == null ? 'Scan' : `Scan ${sequence}`;
}

// Traffic-light tone per DM decision. One record per instruction so a pill and the
// timeline circle for the same scan can never drift apart.
export interface ScanTone {
    base: string;
    selected: string;
    border: string;
    pill: string;
}

/** Scans DM hasn't issued a decision for yet. */
const INSTRUCTION_TONE_NONE: ScanTone = {
    base: 'border-gray-300 bg-white text-gray-700',
    selected: 'border-gray-600 bg-gray-200 text-gray-900',
    border: 'border-gray-300',
    pill: 'bg-gray-50 text-gray-700'
};

const INSTRUCTION_TONE: Record<ScanDecision, ScanTone> = {
    go: {
        base: 'border-emerald-400 bg-emerald-50 text-emerald-800',
        selected: 'border-emerald-700 bg-emerald-200 text-emerald-900',
        border: 'border-emerald-400',
        pill: 'bg-emerald-50 text-emerald-700'
    },
    nogo: {
        base: 'border-amber-400 bg-amber-50 text-amber-800',
        selected: 'border-amber-700 bg-amber-200 text-amber-900',
        border: 'border-amber-400',
        pill: 'bg-amber-50 text-amber-700'
    },
    go_back: {
        base: 'border-red-400 bg-red-50 text-red-800',
        selected: 'border-red-700 bg-red-200 text-red-900',
        border: 'border-red-400',
        pill: 'bg-red-50 text-red-700'
    },
    start: INSTRUCTION_TONE_NONE
};

export function getScanTone(decision: ScanDecision | null): ScanTone {
    return decision ? INSTRUCTION_TONE[decision] : INSTRUCTION_TONE_NONE;
}

const COMFORT_TONE: ScanTone = {
    base: 'border-sky-400 bg-sky-50 text-sky-800',
    selected: 'border-sky-700 bg-sky-200 text-sky-900',
    border: 'border-sky-400',
    pill: 'bg-sky-50 text-sky-700'
};

/** Tone for a scan's timeline marker and minimap tick. */
export function getMarkerTone(
    scan: Pick<DmScanSummary, 'type' | 'instruction' | 'seriesStart'>,
    tracksAligners: boolean
): ScanTone {
    if (isComfortScan(scan)) return COMFORT_TONE;
    // On a case with no decisions the tone must stay neutral, not read as "no decision yet".
    return getScanTone(tracksAligners ? scanDecision(scan) : null);
}

/** Shape doubles the instruction signal so it survives without colour. */
export interface ScanShape {
    box: string;
    /** Counter-rotation that keeps a rotated marker's label upright. */
    label: string;
    /** Diagonal shapes need shrinking to match the visual weight of a circle. */
    scale: number;
}

const INSTRUCTION_SHAPE_NONE: ScanShape = { box: 'rounded-full', label: '', scale: 1 };

const INSTRUCTION_SHAPE: Record<ScanDecision, ScanShape> = {
    go: { box: 'rounded-full', label: '', scale: 1 },
    nogo: { box: 'rounded-[5px]', label: '', scale: 0.92 },
    go_back: { box: 'rounded-[4px] rotate-45', label: '-rotate-45', scale: 0.76 },
    start: INSTRUCTION_SHAPE_NONE
};

export function getScanShape(decision: ScanDecision | null): ScanShape {
    return decision ? INSTRUCTION_SHAPE[decision] : INSTRUCTION_SHAPE_NONE;
}

/** Weeks past due where a late scan stops being amber and turns red. */
export const VERY_LATE_DAYS = 21;

export interface LatenessTone {
    text: string;
    line: string;
}

const ON_TIME: LatenessTone = { text: 'text-emerald-600', line: 'bg-emerald-400' };
const LATE: LatenessTone = { text: 'text-amber-600', line: 'bg-amber-400' };
const VERY_LATE: LatenessTone = { text: 'text-red-600', line: 'bg-red-400' };

// `late` is the backend's verdict, grace period included; re-deriving it here would
// let the connector disagree with the LatePill on the same row.
export function getLatenessTone(late: boolean, lateDays: number | null): LatenessTone {
    if (!late) return ON_TIME;
    return (lateDays ?? 0) > VERY_LATE_DAYS ? VERY_LATE : LATE;
}
